import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Tab, Tabs } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  GetPriceTypeDto,
  CreateUpdatePricingDto,
  CreateUpdatePriceDtoConsultationTypeEnum,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';

import { PricingServicesTab, PriceFilterType } from './PricingServicesTab';
import { PricingBasicDataTab } from './PricingBasicDataTab';

interface PricingEditorProps {
  pricing: CreateUpdatePricingDto;
  handleClose?: () => void;
  show?: boolean;
  id?: string;
  onCompleted: (
    pricing: CreateUpdatePricingDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const PricingEditor: React.FC<PricingEditorProps> = props => {
  const spd = useSpdCore();
  const [pricing, setPricing] = useState(props.pricing);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [combinations, setCombinations] = useState<GetPriceTypeDto[]>([]);

  useEffect(() => {
    spd.pricing
      .pricingControllerGetAllCombinations()
      .then(setCombinations)
      .catch(error => {
        console.error('Błąd ładowania kombinacji cen', error);
      });
  }, [spd.pricing]);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setPricing(props.pricing);
    }
  }, [props.pricing, props.show]);

  const propertyChange = (obj: Partial<CreateUpdatePricingDto>) => {
    setPricing(prev => ({ ...prev, ...obj }));
  };

  const updatePriceItem = (
    index: number,
    price: string,
    priceType?: GetPriceTypeDto,
  ) => {
    const updated = [...(pricing.prices || [])];
    if (!updated[index] && priceType) {
      updated[index] = {
        ...priceType,
        consultationType:
          priceType.consultationType as unknown as CreateUpdatePriceDtoConsultationTypeEnum,
        price: price,
      };
    } else if (updated[index]) {
      updated[index] = { ...updated[index], price: price };
    }
    setPricing(prev => ({ ...prev, prices: updated }));
  };

  const createPricing = async () => {
    try {
      const filteredPricelist = (pricing.prices || []).filter(
        item => item.price && item.price.trim() !== '',
      );
      const saveStatus = await props.onCompleted(
        {
          ...pricing,
          prices: filteredPricelist,
        },
        props.id,
      );
      if (!saveStatus.saved) {
        setSaveErrors(saveStatus.errors);
        setShowSaveErrors(true);
      }
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors([`Błąd podczas tworzenia cennika: ${errors.join(', ')}`]);
      setShowSaveErrors(true);
    }
  };

  const hasCompany = !!pricing?.companyId;

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered
        keyboard
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id ? 'Edycja cennika' : 'Dodawanie cennika'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-2">
            <Card.Body>
              <Tabs defaultActiveKey="basic" className="mb-2">
                <Tab eventKey="basic" title="Dane podstawowe" className="mb-2">
                  <PricingBasicDataTab
                    pricing={pricing}
                    hasCompany={hasCompany}
                    pricingId={props.id}
                    onPropertyChange={propertyChange}
                  />
                </Tab>
                <Tab
                  eventKey="consultationType"
                  title="Typy konsultacji"
                  className="mb-2"
                >
                  <PricingServicesTab
                    combinations={combinations}
                    pricelistItems={pricing.prices || []}
                    onUpdatePrice={updatePriceItem}
                    priceFilterType={PriceFilterType.ConsultationType}
                  />
                </Tab>
                <Tab
                  eventKey="pricingGroup"
                  title="Grupy cenowe"
                  className="mb-2"
                >
                  <PricingServicesTab
                    combinations={combinations}
                    pricelistItems={pricing.prices || []}
                    onUpdatePrice={updatePriceItem}
                    priceFilterType={PriceFilterType.PricingGroup}
                  />
                </Tab>
                <Tab
                  eventKey="supportingExamination"
                  title="Badania pomocnicze"
                  className="mb-2"
                >
                  <PricingServicesTab
                    combinations={combinations}
                    pricelistItems={pricing.prices || []}
                    onUpdatePrice={updatePriceItem}
                    priceFilterType={PriceFilterType.SupportingExamination}
                  />
                </Tab>
                <Tab eventKey="article" title="Artykuły" className="mb-2">
                  <PricingServicesTab
                    combinations={combinations}
                    pricelistItems={pricing.prices || []}
                    onUpdatePrice={updatePriceItem}
                    priceFilterType={PriceFilterType.Article}
                  />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Anuluj
          </Button>
          <Button variant="outline-primary" onClick={createPricing}>
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
